import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRouter from './routes/AppRouter';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DictionaryProvider } from './contexts/DictionaryContext'; 
import { logErrorToService } from './utils/logService';

window.deferredPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  window.deferredPrompt = e;
  console.log('[Global] Evento de instalação Android capturado.');
});

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    logErrorToService('error', this.props.portal, error, this.props.user, 'REACT_ERROR', errorInfo.componentStack);
  }
  render() {
    if (this.state.hasError) return <div className="p-8 text-center text-red-500 font-bold bg-white dark:bg-gray-800 rounded shadow m-4">Ocorreu um erro na renderização da página. A equipe técnica foi notificada automaticamente.</div>;
    return this.props.children;
  }
}

const AppContent = () => {
  const { user } = useAuth(); 

  useEffect(() => {
    const originalConsoleWarn = console.warn;
    const originalConsoleError = console.error;

    const safeLogToString = (arg) => {
      if (arg instanceof Error) return arg.message; 
      if (typeof arg === 'object' && arg !== null) {
        try { return JSON.stringify(arg); } catch (e) { return String(arg); }
      }
      return String(arg); 
    };

    console.warn = (...args) => {
      originalConsoleWarn.apply(console, args);
      const message = args.map(safeLogToString).join(' ');
      logErrorToService('warn', 'portal-servidor', new Error(message), user, 'CONSOLE_WARN');
    };

    console.error = (...args) => {
      originalConsoleError.apply(console, args);
      const errorObject = args.find(arg => arg instanceof Error) || new Error(args.map(safeLogToString).join(' '));
      logErrorToService('error', 'portal-servidor', errorObject, user, 'CONSOLE_ERROR');
    };

    // CORREÇÃO: addEventListener('error') passa um ErrorEvent único como argumento
    const handleGlobalError = (event) => {
      const actualError = event.error || new Error(event.message || 'Erro global desconhecido');
      logErrorToService('error', 'portal-servidor', actualError, user, 'JS_ERROR');
    };

    const handlePromiseRejection = (event) => {
      const actualError = event.reason instanceof Error ? event.reason : new Error(String(event.reason || 'Unhandled promise rejection'));
      logErrorToService('error', 'portal-servidor', actualError, user, 'PROMISE_ERROR');
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handlePromiseRejection);

    return () => {
      console.warn = originalConsoleWarn;
      console.error = originalConsoleError;
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
    };
  }, [user]);

  return (
    <ErrorBoundary portal="portal-servidor" user={user}>
      <AppRouter />
    </ErrorBoundary>
  );
};

function App() {
  const storageKey = 'sisfol_municipio_persist';
  const searchParams = new URLSearchParams(window.location.search);
  const urlId = searchParams.get('municipioId');

  if (urlId) {
      localStorage.setItem(storageKey, urlId);
  }

  useEffect(() => {
    const storedId = localStorage.getItem(storageKey);
    if (!urlId && storedId) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('municipioId', storedId);
        window.history.replaceState({}, '', newUrl.toString());
    }
  }, [urlId]);

  return (
    <ThemeProvider>
      <AuthProvider>
        <DictionaryProvider>
            <NotificationProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <div className="bg-gray-100 dark:bg-gray-900 min-h-screen">
                  <AppContent />
                </div>
              </Router>
            </NotificationProvider>
        </DictionaryProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;